import axios from 'axios';
import {
    clearStoredAuthTokens,
    clearStoredRedirectForAudience,
    getActiveAudience,
    getStoredRefreshToken,
    getStoredTokenForAudience,
    setStoredRedirectForAudience,
    setStoredRefreshToken,
    setStoredTokenForRole,
} from '../utils/authStorage';

// 建立 Axios 實例
// 使用環境變數設定 API 網址
// Fallback Priority:
// 1. VITE_API_BASE_URL (If set)
// 2. Production Backend (Hardcoded fallback for Railway)
// 3. Localhost (Dev fallback)

const PRODUCTION_BACKEND = 'https://api.naturaledu.net';
let calculatedBaseURL = import.meta.env.VITE_API_BASE_URL;

if (!calculatedBaseURL || calculatedBaseURL === '/') {
    // If not set, check if we are in production environment
    if (import.meta.env.PROD) {
        calculatedBaseURL = PRODUCTION_BACKEND;
    } else if (typeof window !== 'undefined' && (window.location.hostname.includes('railway.app') || window.location.hostname.includes('naturaledu.net'))) {
        calculatedBaseURL = PRODUCTION_BACKEND;
    } else {
        calculatedBaseURL = 'http://localhost:8000';
    }
}

// 強制 HTTPS
if (typeof window !== 'undefined' && window.location.protocol === 'https:' && !calculatedBaseURL.startsWith('https')) {
    calculatedBaseURL = calculatedBaseURL.replace(/^http:\/\//i, 'https://');
}

// Export the calculated URL
export const API_BASE_URL = calculatedBaseURL.replace(/\/$/, "");
export const baseURL = API_BASE_URL; // For backward compatibility

// Cloudflare Worker 代理快取網域（見 cloudflare-worker-cloudinary-proxy/），
// 把 Cloudinary 圖片網址置換過去，讓重複瀏覽從 Cloudflare 邊緣快取吐圖，省 Cloudinary bandwidth 額度
export const CLOUDINARY_PROXY_HOST = 'img.naturaledu.net';

// 統一的圖片網址解析：Cloudinary 網址 → 代理網域；其餘絕對網址原樣；相對路徑補上 API_BASE_URL
export const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('blob:')) return url; // 送出前的本機預覽，尚未真正上傳
    if (url.includes('res.cloudinary.com')) {
        return url.replace(/^https?:\/\/res\.cloudinary\.com/, `https://${CLOUDINARY_PROXY_HOST}`);
    }
    return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
};

const api = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
    },
    // Custom serializer for FastAPI compatibility: arrays -> key=val&key=val
    paramsSerializer: params => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
            const val = params[key];
            if (Array.isArray(val)) {
                val.forEach(v => searchParams.append(key, v));
            } else if (val !== undefined && val !== null) {
                searchParams.append(key, val);
            }
        }
        return searchParams.toString();
    },
});

let isRedirectingToLogin = false;

// C3 auth 翻新（spec 4.3）：/auth/token（新版登入）與 /auth/refresh（換票本身）都要跳過
// 401→refresh-and-retry／nuclear 登出邏輯，理由分別是：
// - /auth/token：帳密打錯就是打錯，跟 /auth/login 一樣要讓 AuthContext.login() 自己接住錯誤訊息，
//   不能被攔截器搶先清 storage + 整頁導頁（否則登入表單的錯誤訊息 UI 永遠來不及顯示）。
// - /auth/refresh：換票請求本身失敗＝真正失效，必須直接判定為 refresh 失敗（見下方
//   refreshAccessToken）而不是被攔截器當成一般 401 又試著呼叫 refresh 一次，會遞迴。
const shouldBypassAuthRedirect = (config = {}) => {
    const url = config.url || '';
    return [
        '/auth/login',
        '/auth/token',
        '/auth/verify-activation',
        '/auth/complete-activation',
        '/auth/refresh',
    ].some((path) => url.includes(path));
};

// AuthContext 掛載後會呼叫 registerAuthStateHandlers 註冊回呼，讓這裡 refresh 成功時能同步
// 更新 React 的 token state（spec 4.5 第 1 點：WebSocketContext 的 useEffect 依賴 [token, user]，
// token state 更新後會自動 cleanup 舊連線並用新 token 重連，不需要另外手動處理 WS 重連）。
let authStateHandlers = {
    onTokenRefreshed: null, // (newAccessToken: string) => void
};

export const registerAuthStateHandlers = (handlers) => {
    authStateHandlers = { ...authStateHandlers, ...handlers };
};

// 現行「登出」的本質：清 storage + 整頁硬導頁（見 spec 1.7）。這個 nuclear 選項本身完整保留，
// 只是現在有多個入口會走到它：(a) 攔截器沒有 refresh token 可用、(b) 攔截器 refresh 請求本身
// 也失敗、(c) AuthContext 的 visibilitychange resume 檢查（spec 4.4）主動 refresh 也失敗——
// 三者都代表 session 真正失效，統一走同一個 export，避免各處各自兜一套清理+導頁邏輯。
export const performNuclearLogout = async () => {
    if (isRedirectingToLogin || typeof window === 'undefined') return;
    isRedirectingToLogin = true;

    const activeAudience = getActiveAudience();
    await clearStoredAuthTokens();
    clearStoredRedirectForAudience(activeAudience);

    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath && currentPath !== '/login') {
        setStoredRedirectForAudience(currentPath, activeAudience);
    }

    window.location.assign('/login');
};

// Module-level single-flight refresh（spec 4.3）：所有同時觸發 401 的 request 共用同一個
// refreshPromise，靠模組層級變數天然達成，不需要額外的鎖套件。也供 AuthContext 的
// visibilitychange resume 檢查（spec 4.4）直接呼叫，兩邊共用同一份實作與同一個 in-flight guard。
//
// 硬要求（C2 審核帶下來）：refresh 成功後「先把新 refresh token 持久化寫入 storage、
// 再讓這個 promise resolve」——resolve 之後才會有任何呼叫端（攔截器的重試佇列、resume 檢查）
// 繼續往下走，天然保證 store-then-resolve 順序，避免「刷新成功但 App 在寫入前被殺」導致
// 下次啟動拿舊 refresh token 撞後端 reuse detection。
let refreshPromise = null;

export const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const currentRefreshToken = await getStoredRefreshToken();
            if (!currentRefreshToken) {
                // 涵蓋舊版單 token 使用者／從未升級的情境：沒有 refresh token 可用，
                // 呼叫端（攔截器／resume 檢查）收到這個 rejection 後一律視同「真失效」。
                throw new Error('NO_REFRESH_TOKEN_AVAILABLE');
            }

            const res = await api.post('/auth/refresh', { refresh_token: currentRefreshToken });
            const { access_token: newAccessToken, refresh_token: newRefreshToken } = res.data;

            await setStoredTokenForRole(newAccessToken);
            await setStoredRefreshToken(newRefreshToken);

            if (typeof authStateHandlers.onTokenRefreshed === 'function') {
                authStateHandlers.onTokenRefreshed(newAccessToken);
            }

            return newAccessToken;
        })().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

// 請求攔截器：async 讀 token（Preferences 為非同步 API，見 authStorage.js）
api.interceptors.request.use(
    async (config) => {
        const token = await getStoredTokenForAudience();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 回應攔截器（spec 4.3，原本重複註冊的兩個 401 interceptor 於此整併為一個）：
// 401 且不在白名單內 → 已重試過一次就直接 nuclear 登出；否則先試一次 single-flight refresh，
// 成功就帶新 token 重打原請求，refresh 失敗（含沒有 refresh token 可用）才是「真失效」。
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;

        if (!response || response.status !== 401 || shouldBypassAuthRedirect(config)) {
            return Promise.reject(error);
        }

        if (config._retry) {
            await performNuclearLogout();
            return Promise.reject(error);
        }

        try {
            const newAccessToken = await refreshAccessToken();
            config._retry = true;
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(config);
        } catch (refreshError) {
            console.warn('Refresh token 失效或不可用，導向登入頁:', refreshError.message);
            await performNuclearLogout();
            return Promise.reject(error);
        }
    }
);

export const authAPI = {
    // C3 auth 翻新：改打新版 /auth/token（spec 3.1），拿 access+refresh 配對。
    // 帳密驗證邏輯與舊 /auth/login 完全共用同一個後端函式（authenticate_user），
    // 回應格式是 /login 的超集（多一個 refresh_token 欄位），舊版 /login 端點本身不受影響、
    // 也不再被本檔案呼叫（唯一呼叫方是 AuthContext.login()，見 grep 確認）。
    login: (phoneNumber, password) => {
        // 改為 x-www-form-urlencoded 格式
        const formData = new URLSearchParams();
        formData.append('username', phoneNumber);
        formData.append('password', password);

        return api.post('/auth/token', formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });
    },
    verifyActivation: (phoneNumber, studentName) =>
        api.post('/auth/verify-activation', { phone_number: phoneNumber, student_name: studentName }),
    completeActivation: (tempToken, password) =>
        api.post('/auth/complete-activation', { temp_token: tempToken, password }),

    // Auth - Unregister Device (Stop Push)
    unregisterDevice: (token) => api.post('/auth/unregister-device', { fcm_token: token }),

    // SSO - 取得跨系統短效 Token（前往教育平台用）
    getSSOToken: () => api.get('/auth/sso-token'),

    // --- 用戶管理 (Admin) ---
    getIcons: () => api.get('/users/me'),
    getMe: () => api.get('/users/me'),
    deleteMe: () => api.delete('/users/me'),
    updatePassword: (data) => api.put('/users/me/password', data),
    // [A3] 車號管理（家長）：上限 2 組，後端 service 層做 normalize/去重/長度驗證
    updateCarPlates: (carPlates) => api.put('/users/me/car-plates', { car_plates: carPlates }),
    getUsers: (params) => api.get('/users/', { params }),
    createUser: (data) => api.post('/admin/staff/', data),
    scanParents: () => api.post('/admin/ops/scan-parents'),
    deleteUser: (id) => api.delete(`/users/${id}`),
    updateUser: (id, data) => api.put(`/users/${id}`, data),
    smartUpdateParent: (data) => api.post('/users/smart-update-parent', data),

    // --- 出勤打卡 (Attendance) ---
    attendance: {
        checkIn: (note) => api.post('/attendance/check-in', null, { params: { note } }),
        checkOut: (note) => api.post('/attendance/check-out', null, { params: { note } }),
        getStatus: () => api.get('/attendance/status'),
        getHistory: (params) => api.get('/attendance/history', { params }),
        exportHistory: (params) => api.get('/attendance/export', { params, responseType: 'blob' }),

        // Phase 26: Class Attendance
        getClassAttendance: (classId, date) => api.get(`/attendance/class/${classId}`, { params: { date } }),
        batchUpdateClassAttendance: (classId, studentIds, date, status) =>
            api.post('/attendance/batch', {
                class_id: classId,
                student_ids: studentIds,
                date: date, // YYYY-MM-DD
                status: status
            }),
    },

    // --- 報表中心 (Reports) ---
    frontdesk: {
        scan: (scanCode, eventSource = 'BARCODE_SCANNER') =>
            api.post('/frontdesk/scan', { scan_code: scanCode, event_source: eventSource }),
        getRecentEvents: (limit = 20) => api.get('/frontdesk/recent-events', { params: { limit } }),
        getEvents: (params) => api.get('/frontdesk/events', { params }),
        getStudentScanCode: (studentId) => api.get(`/frontdesk/students/${studentId}/scan-code`),
        confirmLeaveCheckin: (studentId, note = null) =>
            api.post('/frontdesk/confirm-leave-checkin', { student_id: studentId, note }),
        issueScanCode: (studentId, code = null) =>
            api.post(`/frontdesk/students/${studentId}/scan-code`, { code }),
        // 多磁扣管理（磁扣管理 modal 用，一人最多 3 張）
        listScanCodes: (studentId) => api.get(`/frontdesk/students/${studentId}/scan-codes`),
        addScanCode: (studentId, code) =>
            api.post(`/frontdesk/students/${studentId}/scan-codes`, { code }),
        removeScanCode: (scanCodeId) => api.delete(`/frontdesk/scan-codes/${scanCodeId}`),
    },

    getAttendanceReport: (params) => api.get('/reports/attendance-export', { params, responseType: 'blob' }),
    getClassAttendanceReport: (classId, startDate, endDate) =>
        api.get('/reports/class-attendance-export', {
            params: { class_id: classId, start_date: startDate, end_date: endDate },
            responseType: 'blob'
        }),
    getBarcodeSheet: (params) => api.get('/reports/barcode-sheet', { params, responseType: 'blob' }),

    // --- 機構管理 (Super Admin) ---
    getInstitutions: () => api.get('/admin/institutions/'),
    getInstitution: (id) => api.get(`/admin/institutions/${id}`),
    createInstitution: (data) => api.post('/admin/institutions/', data),
    updateInstitution: (id, data) => api.put(`/admin/institutions/${id}`, data),
    // [A1] super_admin 切換目前檢視的機構；回傳 { access_token, token_type, user }
    switchInstitution: (institutionId) => api.post('/admin/switch-institution', { institution_id: institutionId }),
    // [2026-08-20] 教職員通知設定的可選項目
    // 回傳 { roles: [{name,label}], capabilities: [{name,group,label,description,default_roles}] }
    // 說明文字與預設值一律由後端提供——寫死在前端的話，後端改了預設值而前端那句說明
    // 不會跟著改，使用者看到的與系統實際做的就不一致了。
    // 機構目前的設定值則在 getInstitution() 回應的 staff_permissions（已解析成完整矩陣）。
    getStaffPermissionOptions: () => api.get('/admin/staff-permission-options'),
    triggerDailyReset: () => api.post('/admin/trigger-daily-reset'),

    // --- 班級管理 (Admin) ---
    getClassesLite: (institutionId = null) => {
        let url = '/classes/lite';
        if (institutionId) url += `?institution_id=${institutionId}`;
        return api.get(url);
    },
    getClasses: (params) => api.get('/classes/', { params }),
    createClass: (data) => api.post('/classes/', data),
    getClass: (id) => api.get(`/classes/${id}`),
    updateClass: (id, data) => api.put(`/classes/${id}`, data),
    deleteClass: (id) => api.delete(`/classes/${id}`),
    previewClassArchive: (id) => api.get(`/classes/${id}/archive-preview`),
    archiveClassWithStudents: (id, studentIds) => api.post(`/classes/${id}/archive-with-students`, { student_ids: studentIds }),

    // --- 課表管理 ---
    addClassSchedule: (classId, data) => api.post(`/classes/${classId}/schedules`, data),
    deleteClassSchedule: (classId, scheduleId) => api.delete(`/classes/${classId}/schedules/${scheduleId}`),
    addClassException: (classId, data) => api.post(`/classes/${classId}/exceptions`, data),
    deleteClassException: (classId, exceptionId) => api.delete(`/classes/${classId}/exceptions/${exceptionId}`),

    // --- 學生管理 (Teachers / Admin) ---
    getStudents: (params) => api.get('/teachers/students/', { params }),
    getDailySummary: () => api.get('/teachers/daily-summary'),
    createStudent: (data) => api.post('/teachers/students/', data),
    getStudent: (id) => api.get(`/teachers/students/${id}`),
    updateStudent: (id, data) => api.put(`/teachers/students/${id}`, data),
    bindParent: (id, data) => api.post(`/teachers/students/${id}/parents`, data),
    updateStudentStatus: (id, status) => api.patch(`/teachers/students/${id}/status`, { status }),
    batchUpdateStudentStatus: (studentIds, status) => api.put('/teachers/students/batch/status', { student_ids: studentIds, status }),
    deleteStudent: (id) => api.delete(`/teachers/students/${id}`),

    // --- 舊生（已離校）---
    getArchivedStudents: (search) => api.get('/teachers/students/archived', { params: search ? { search } : {} }),
    archiveStudent: (id) => api.patch(`/teachers/students/${id}/archive`),
    restoreStudent: (id, classId) => api.patch(`/teachers/students/${id}/restore`, { class_id: classId }),

    // --- 點名與接送 (Teachers / Parents) ---
    // [A3] carPlate 為 optional 第二參數，向後相容：不傳時行為與原本逐字相同
    // （車號 feature 關閉的機構/舊版呼叫端都不會帶這個參數）。
    startPickup: (studentId, carPlate = null) =>
        api.post('/pickups/start', carPlate ? { student_id: studentId, car_plate: carPlate } : { student_id: studentId }),
    completePickup: (studentId) => api.post('/pickups/complete', { student_id: studentId }),
    cancelPickup: (studentId, reason = null) => api.post('/pickups/cancel', { student_id: studentId, reason }),
    updateGps: (data) => api.post('/pickups/gps/update', data),
    getRealtimePredictions: () => api.get('/pickups/predictions/realtime'),

    // --- 廣播 (System) ---
    // 廣播通常走 WebSocket，但如果有手動觸發廣播的 API 也可以加
    broadcastTTS: (text) => api.post('/broadcast/test-tts', { text }),

    // Leaves
    leaves: {
        getLeaves: (params) => api.get('/leaves/', { params }), // Restore trailing slash to prevent Redirect
        createLeave: (data) => api.post('/leaves/', data),
        deleteLeave: (id) => api.delete(`/leaves/${id}`),
        batchCancel: (leaveIds) => api.post('/leaves/batch-cancel', { leave_ids: leaveIds }),
        batchCreate: (data) => api.post('/leaves/batch', data),
        calculateDates: (data) => api.post('/leaves/calculate-dates', data),
    },

    // Notifications
    getNotifications: () => api.get('/notifications/'),
    markNotificationRead: (id) => api.put(`/notifications/${id}/read`),
    markAllNotificationsRead: () => api.post('/notifications/read-all'),
    registerDevice: (token, deviceType = 'web') => api.post('/notifications/devices', { fcm_token: token, device_type: deviceType }),
    testPush: () => api.post('/notifications/test-push'),

    // --- Communication Book (Phase 28) ---
    communicationBook: {
        getStatus: (date) => api.get('/communication-books/status', { params: { query_date: date } }),
        getByClass: (classId, date) => api.get(`/communication-books/class/${classId}`, { params: { query_date: date } }),
        createOrUpdate: (data) => api.post('/communication-books/', data),
        updateBook: (bookId, data) => api.put(`/communication-books/${bookId}`, data),
        updateFeedback: (bookId, data) => api.post(`/communication-books/${bookId}/feedback`, data),
        markSignatureRead: (id, payload) => api.post(`/communication-books/signatures/${id}/read`, payload),
        uploadPhoto: (formData) => api.post('/communication-books/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        // Parent
        // [Hotfix 2026-08-01] params 為新增的選填參數（skip/limit），額外傳入才會出現在
        // query string；不帶 params 呼叫時行為與改動前完全一致（後端預設仍是最新 30 本）。
        getChildHistory: (studentId, params) => api.get(`/communication-books/my-child/${studentId}`, { params }),
        signBook: (bookId, data) => api.post(`/communication-books/sign`, data),

        // Actions
        sendAll: (classId, dateStr, scheduledAt = null) =>
            api.post('/communication-books/send-all', null, {
                params: {
                    class_id: classId,
                    date_str: dateStr,
                    scheduled_at: scheduledAt
                }
            }),
        cancelSchedule: (bookId) => api.delete(`/communication-books/${bookId}/schedule`),
        getMonthlySummary: (classId, year, month) =>
            api.get(`/communication-books/class/${classId}/monthly-summary`, { params: { year, month } }),
    },

    // --- Announcements (Phase 30) ---
    announcements: {
        // 後台 (老師/行政)
        listAdmin: (params) => api.get('/announcements/admin', { params }),
        create: (data) => api.post('/announcements/', data),
        getDetail: (id) => api.get(`/announcements/${id}`),
        update: (id, data) => api.put(`/announcements/${id}`, data),
        publish: (id) => api.post(`/announcements/${id}/publish`),
        archive: (id) => api.post(`/announcements/${id}/archive`),
        delete: (id) => api.delete(`/announcements/${id}`),
        repush: (id) => api.post(`/announcements/${id}/repush`),
        readStats: (id) => api.get(`/announcements/${id}/read-stats`),
        remindUnread: (id) => api.post(`/announcements/${id}/remind-unread`),
        // 家長端
        getActive: (limit = 5) => api.get('/announcements/active', { params: { limit } }),
        getMy: () => api.get('/announcements/my'),
        getMyDetail: (id) => api.get(`/announcements/my/${id}`),
        markRead: (id, source = 'list') => api.post(`/announcements/${id}/read`, { source }),
    },

    // --- Makeup (Phase 27/29) ---
    makeup: {
        batchGenerate: (date) => api.post('/makeup/batch-generate', null, { params: { target_date: date } }),
        updateRecord: (id, data) => api.put(`/makeup/${id}`, data),
        batchUpdateStatus: (recordIds, status) => api.post('/makeup/batch-update-status', { record_ids: recordIds, status }),
        batchDelete: (recordIds) => api.post('/makeup/batch-delete', { record_ids: recordIds }),
    },

    // --- Lunch (Phase 29) ---
    lunch: {
        batchOrder: (data) => api.post('/lunch/batch', data),
        getOrders: (params) => api.get('/lunch/', { params }),
        cancelOrder: (id) => api.delete(`/lunch/${id}`),
        batchCancel: (orderIds) => api.post('/lunch/batch-cancel', { order_ids: orderIds }),
        getStats: (date, institutionId) => api.get('/lunch/stats', { params: { date, institution_id: institutionId } }),
        getMonthlySummary: (year, month, classId) =>
            api.get('/lunch/monthly-summary', {
                params: { year, month, ...(classId ? { class_id: classId } : {}) }
            }),
        exportReport: (classId, year, month) =>
            api.get('/lunch/export', {
                params: { class_id: classId, year, month },
                responseType: 'blob'
            }),
    },

    // --- Chat（P1 親師對話，老師 PWA + 家長共用）---
    // chat_enabled 旗標關閉時，後端這些端點一律回 403（見 app/routers/chat.py _require_chat_enabled）。
    chat: {
        conversations: () => api.get('/chat/conversations'), // 家長：孩子每個課程各一串
        inbox: (params) => api.get('/chat/inbox', { params }), // 老師/管理員收件匣：{scope,filter,q}
        messages: (conversationId, params) => api.get(`/chat/conversations/${conversationId}/messages`, { params }), // {before_id,limit}
        // 單一對話摘要（格式與 /chat/inbox 每一列相同）。給對話室在沒有從列表帶入顯示名稱時
        // （重新整理／FCM 深連結直接進來）用，取代原本整包打 /chat/inbox 再從陣列裡找一條的作法
        // ——後者在管理員 scope=all 情境下等於多查一次全機構範圍，只為了拿一個名字。
        getConversation: (conversationId) => api.get(`/chat/conversations/${conversationId}`),
        send: (conversationId, body) => api.post(`/chat/conversations/${conversationId}/messages`, body), // {content_type,text?,image_url?}
        read: (conversationId) => api.post(`/chat/conversations/${conversationId}/read`),
        // 教職員未讀彙總：{threads_with_unread} ＝ 有新訊息的**對話串數**（一串連傳 5 則仍算 1）。
        // 2026-08-19 user 拍板從「學生人數」改成串數：爸媽各傳一則對老師是兩件要回的事。
        // 回傳同時帶舊欄位名 students_with_unread（值相同），只為了撐過前後端兩次獨立部署的落差，
        // 之後會刪——前端請一律讀 threads_with_unread。
        // 首頁統計卡與底部導航列紅點共用；旗標關閉時後端回 0 而非 403。
        unreadSummary: () => api.get('/chat/unread-summary'),
        // [Stage3 整合] chat 專用圖片上傳（E1 端點：chat_enabled＋對話參與者雙門；家長傳圖走此，
        // 取代會 403 的老師專用聯絡簿上傳）
        // ⚠ 必須明確指定 multipart：api 實例的預設 header 是 'Content-Type: application/json'，
        // 不覆寫的話 FormData 會被當成 JSON 送出，後端收不到 files 欄位而失敗。
        // 老師端用的 communicationBook.uploadPhoto 本來就有指定（api.js:378），
        // 這裡當初漏了，造成「老師傳得了圖、家長傳不了」（user 2026-08-12 實機回報）。
        upload: (conversationId, formData) => api.post(`/chat/conversations/${conversationId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
        // 「可以主動開啟對話的學生」名冊（收件匣下方「尚未開始對話」那一區的資料來源）。
        // 每一列＝{ student_id, student_name, classes: [{id, name}], parents: [{id, name}] }。
        // classes 是**這位呼叫者開得起來的班**（不是該生的全部班級），可以直接拿去當班級選擇的選項。
        // parents 是該生綁定的家長（依 id 由小到大，第一位＝主要家長）：對話已改成每位家長
        // 各一條，所以「可以開的對話」是 (學生 × 班級 × 家長)，前端要據此把一位有爸媽的學生
        // 展開成兩列，否則媽媽那條開過之後爸爸那條就再也沒有入口。
        // parents 為空陣列＝該生尚未綁定家長帳號（後端刻意仍然列出來，老師需要知道
        // 「這個孩子聯絡不到」；點下去會拿到後端一句話說明的 400）。
        // ⚠ 不要改回 getStudents(/teachers/students/)：那支的權限模型與 chat 不同，對管理員
        //   有三個方向的偏差（沒有主班的學生會消失／超管會拿到別機構點下去 403 的學生／
        //   機構未設定時回 400 被前端靜默吞成空名冊）。詳見後端 chat.py list_chat_roster 的說明。
        roster: () => api.get('/chat/roster'),
        // 老師主動開啟（或取得）與某位學生的對話。
        // 【為什麼需要這支】/chat/inbox 只回「已經存在的對話」，而對話只在家長開 App、聯絡簿
        // fanout、群發 fanout 三個時機才會被建出來 —— 「家長從沒開過 App、且該班沒發過聯絡簿」
        // 的學生因此完全不會出現在收件匣，偏偏那正是最需要老師主動聯繫的一群。
        // 回傳結構與 /chat/inbox 的每一列完全相同，拿到可直接塞進收件匣清單、不必另寫解析。
        // 冪等：已存在就回既有那一條（200，不是 201），重複點不會開出第二條。
        // classId（選填）：學生跨多班時指定掛哪一班；不給由後端依規則挑（優先已有對話的班）。
        // parentId（選填）：開給哪一位家長。不給時後端退回主要家長（綁定家長中 id 最小者）。
        //   **呼叫端一律明講**：名冊已經把每位家長各展開成一列，這個值必定是知道的；
        //   讓後端猜等於有一半機率開錯人，而且後續訊息會送給不該收到的那一位。
        //   相容路徑保留只是為了不讓快取住舊 bundle 的老師端「按了沒反應」。
        // ⚠ 兩者都是 query 參數不是 body：這支端點沒有 request body，直接串在網址上
        //   （比照本檔 getClassesLite 的寫法），避免 axios 為了帶 params 而多送一個空 body。
        openStudentConversation: (studentId, classId, parentId) => {
            const qs = [
                ['class_id', classId],
                ['parent_id', parentId],
            ]
                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
                .join('&');
            return api.post(`/chat/students/${studentId}/conversation${qs ? `?${qs}` : ''}`);
        },
        // 回收自己發出的訊息（軟刪除：後端把 text/image_url 清成 null、is_recalled=true，留墓碑）。
        // ⚠ 「超過 24 小時」與「聯絡簿訊息不可回收」後端回的是 400 而不是 403 —— 因為本檔開頭
        //   說的那條規則：chat 端點的 403 在前端語意是「chat_enabled 旗標關閉」。呼叫端請把
        //   err.response?.data?.detail 顯示出來，那句話是寫給老師看的（例如「超過 24 小時的
        //   訊息無法收回」），用一句籠統的「操作失敗」蓋掉會讓老師以為系統壞了而重複嘗試。
        recallMessage: (messageId) => api.delete(`/chat/messages/${messageId}`),
        // 整班群發：一次呼叫，後端逐生落一則 is_broadcast=true 的訊息進各自的對話，
        // 家長端一人只收到一則推播、教職員不收（規則全在後端，前端不必也不該自己去湊 N 次 send）。
        // 權限同樣由後端把關：帶班老師限自己帶的班、行政/管理員可對機構內任意班。
        // 回傳 { sent, pushed }；空班級是 sent=0 的 200，不是錯誤。
        broadcast: (classId, body) => api.post(`/chat/classes/${classId}/broadcast`, body), // {content_type,text?,image_url?}

        // ── BL-006 關鍵詞過濾器（機構設定，只有 admin/super_admin 進得去）──
        // ⚠ 這幾支**不吃 chat_enabled 旗標**（後端刻意如此：機構要能在旗標打開前先備好詞表），
        //   所以它們的 403 語意跟上面那些不一樣——這裡的 403 是**真的沒有權限**（非管理員），
        //   不是「功能未開放」。呼叫端不要套用本檔開頭那條「403＝旗標關閉」的解讀。
        // ⚠ 新增失敗時務必顯示 err.response?.data?.detail：後端的 400 訊息寫的是**為什麼**
        //   （例如「詞語至少要 2 個字…只設一個『不』字會連『不會』『不用』都一起命中」），
        //   用籠統的「新增失敗」蓋掉，管理員只會換個字再試一次而不明白哪裡不同。
        keywordRules: () => api.get('/chat/keyword-rules'),          // { rules, min_length, max_rules }
        addKeywordRule: (keyword) => api.post('/chat/keyword-rules', { keyword }),
        setKeywordRuleActive: (ruleId, isActive) =>
            api.patch(`/chat/keyword-rules/${ruleId}`, { is_active: isActive }),
        deleteKeywordRule: (ruleId) => api.delete(`/chat/keyword-rules/${ruleId}`),
        // 命中紀錄（供「上個月被攔到幾次、都是什麼詞」）。
        // 後端刻意不回傳訊息內容，只給詞、學生、時間與 conversation_id。
        keywordHits: (params) => api.get('/chat/keyword-hits', { params }), // {days,limit}
    },

    // Others (Duplicates Removed)
};

export default api;
